import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Clock, MapPin, Plus, Pencil, Trash2, Lock, ChefHat, Copy, Printer, Users, X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const DAYS = { fr: ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"], nl: ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"], en: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] };
const MONTHS = { fr: ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"], nl: ["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"], en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"] };
const MOMENTS = { fr: ["Matin", "Après-midi"], nl: ["Ochtend", "Namiddag"], en: ["Morning", "Afternoon"] };
const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const mondayOf = (d) => { const x = new Date(d); const dow = x.getDay(); x.setDate(x.getDate() + (dow === 0 ? -6 : 1 - dow)); x.setHours(0, 0, 0, 0); return x; };
const emptyForm = { id: null, name: "", start: "", end: "", place: "", responsables: "", day: 0, moment: "morning" };

export default function CuisinePlanning() {
  const { i18n, t } = useTranslation();
  const { user } = useAuth();
  const lng = (i18n.language || "fr").slice(0, 2);
  const L = DAYS[lng] || DAYS.fr, ML = MONTHS[lng] || MONTHS.fr, MO = MOMENTS[lng] || MOMENTS.fr;
  const [monday, setMonday] = useState(mondayOf(new Date()));
  const [entries, setEntries] = useState([]);
  const [maison, setMaison] = useState("");
  const [form, setForm] = useState({ ...emptyForm });
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyTarget, setCopyTarget] = useState("");

  const load = useCallback(async (mon) => {
    try { const { data } = await api.get("/cuisine/entries", { params: { week_start: iso(mon) } }); setEntries(data.entries || []); if (data.maison) setMaison(data.maison); }
    catch { setEntries([]); }
  }, []);
  useEffect(() => { load(monday); }, [monday, load]);
  useEffect(() => { setForm((f) => ({ ...f, responsables: f.responsables || user?.name || "" })); }, [user]);

  const shift = (days) => { const d = new Date(monday); d.setDate(d.getDate() + days); setMonday(mondayOf(d)); };
  const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
  const period = `${t("cuisine.fromMonday")} ${pad(monday.getDate())} ${ML[monday.getMonth()]} ${t("cuisine.toSunday")} ${pad(sunday.getDate())} ${ML[sunday.getMonth()]} ${sunday.getFullYear()}`;
  const cell = (di, mo) => entries.filter((e) => e.day === di && e.moment === mo);

  const save = async () => {
    if (!form.name.trim()) return toast.error(t("cuisine.nameRequired"));
    const payload = { week_start: iso(monday), day: form.day, moment: form.moment, name: form.name.trim(), start: form.start, end: form.end, place: form.place, responsables: form.responsables };
    try {
      if (form.id) { await api.put(`/cuisine/entries/${form.id}`, payload); toast.success(t("cuisine.taskUpdated")); }
      else { await api.post("/cuisine/entries", payload); toast.success(t("cuisine.taskSaved")); }
      setForm({ ...emptyForm, responsables: user?.name || "" });
      await load(monday);
    } catch (e) { toast.error(e?.response?.data?.detail || t("cuisine.saveFailed")); }
  };
  const edit = (e) => { if (!e.can_edit) return; setForm({ id: e.id, name: e.name || "", start: e.start || "", end: e.end || "", place: e.place || "", responsables: e.responsables || "", day: e.day, moment: e.moment }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const remove = async (e) => {
    if (!e.can_edit) return;
    if (!window.confirm(t("cuisine.confirmDelete", { name: e.name }))) return;
    try { await api.delete(`/cuisine/entries/${e.id}`); toast.success(t("cuisine.taskDeleted")); await load(monday); }
    catch (err) { toast.error(err?.response?.data?.detail || t("cuisine.saveFailed")); }
  };
  const copyWeek = async () => {
    if (!copyTarget) return;
    const target = iso(mondayOf(new Date(copyTarget + "T00:00:00")));
    try {
      const { data } = await api.post("/cuisine/entries/copy-week", { from_week: iso(monday), to_week: target });
      toast.success(t("cuisine.copyDone", { count: data.copied }));
      setCopyOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || t("cuisine.saveFailed")); }
  };

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="cuisine-planning">
      <style>{`@media print { @page { size: A4 landscape; margin: 8mm; } body * { visibility: hidden !important; } #cuisine-print, #cuisine-print * { visibility: visible !important; } #cuisine-print { display: block !important; position: fixed; left: 0; top: 0; width: 100%; } }`}</style>

      <div className="rounded-3xl p-6 sm:p-8 relative overflow-hidden glass">
        <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold" style={{ background: "rgba(249,115,22,0.16)", color: "#fdba74" }}>
          <ChefHat className="h-3.5 w-3.5" /> {t("cuisine.title")}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-2">{t("nav.cuisinePlanning")}</h1>
        <p className="text-muted-foreground text-sm mt-1">{t("cuisine.planningSubtitle")}</p>
      </div>

      <Card className="p-4 sm:p-5 glass">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>{t("planning.residence")}</Label>
            <Input value={maison} readOnly className="mt-1 opacity-80" data-testid="cuisine-planning-maison" />
          </div>
          <div>
            <Label>{t("cuisine.weekOfMonday")}</Label>
            <div className="flex items-center gap-2 mt-1">
              <Button size="icon" variant="outline" data-testid="cuisine-planning-prev" onClick={() => shift(-7)}><ChevronLeft className="h-4 w-4" /></Button>
              <Input type="date" value={iso(monday)} onChange={(e) => { const d = new Date(e.target.value + "T00:00:00"); if (!isNaN(d)) setMonday(mondayOf(d)); }} data-testid="cuisine-planning-week" />
              <Button size="icon" variant="outline" data-testid="cuisine-planning-next" onClick={() => shift(7)}><ChevronRight className="h-4 w-4" /></Button>
              <Button variant="outline" data-testid="cuisine-planning-today" onClick={() => setMonday(mondayOf(new Date()))}>{t("planning.today")}</Button>
            </div>
          </div>
          <div>
            <Label>{t("cuisine.period")}</Label>
            <Input value={period} readOnly className="mt-1 opacity-80" />
          </div>
        </div>
      </Card>

      <Card className="p-4 sm:p-5 glass" data-testid="cuisine-planning-form">
        <h2 className="font-display font-bold text-lg mb-3">{form.id ? t("cuisine.editTask") : t("cuisine.addTask")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2"><Label>{t("cuisine.taskName")}</Label>
            <Input data-testid="cuisine-planning-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("cuisine.taskPlaceholder")} className="mt-1" /></div>
          <div><Label>{t("planning.start")}</Label><Input value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} placeholder="10h30" className="mt-1" /></div>
          <div><Label>{t("planning.end")}</Label><Input value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} placeholder="11h30" className="mt-1" /></div>
          <div><Label>{t("planning.place")}</Label><Input value={form.place} onChange={(e) => setForm({ ...form, place: e.target.value })} placeholder={t("cuisine.placePlaceholder")} className="mt-1" /></div>
          <div><Label>{t("cuisine.responsables")}</Label><Input data-testid="cuisine-planning-responsables" value={form.responsables} onChange={(e) => setForm({ ...form, responsables: e.target.value })} className="mt-1" /></div>
          <div><Label>{t("planning.day")}</Label>
            <select className="w-full h-10 rounded-md border px-3 bg-background mt-1" data-testid="cuisine-planning-day" value={form.day} onChange={(e) => setForm({ ...form, day: Number(e.target.value) })}>{L.map((d, i) => <option key={i} value={i}>{d}</option>)}</select></div>
          <div><Label>{t("planning.moment")}</Label>
            <select className="w-full h-10 rounded-md border px-3 bg-background mt-1" value={form.moment} onChange={(e) => setForm({ ...form, moment: e.target.value })}><option value="morning">{MO[0]}</option><option value="afternoon">{MO[1]}</option></select></div>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <Button data-testid="cuisine-planning-save" onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-5">
            <Plus className="h-4 w-4 mr-1" /> {form.id ? t("planning.editSave") : t("planning.add")}
          </Button>
          {form.id && <Button variant="ghost" onClick={() => setForm({ ...emptyForm, responsables: user?.name || "" })}>{t("planning.cancel")}</Button>}
          <span className="text-xs text-muted-foreground">{t("cuisine.taskHint")}</span>
        </div>
      </Card>

      <Card className="p-4 sm:p-5 glass">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h2 className="font-display font-bold text-lg">{t("cuisine.weekOverview")} — {maison} ({entries.length})</h2>
            <p className="text-xs text-muted-foreground">{period}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" data-testid="cuisine-copy-week-btn" onClick={() => { setCopyTarget(""); setCopyOpen(true); }}><Copy className="h-4 w-4 mr-1.5" /> {t("cuisine.copyWeek")}</Button>
            <Button variant="outline" data-testid="cuisine-print-btn" onClick={() => window.print()}><Printer className="h-4 w-4 mr-1.5" /> {t("cuisine.print")}</Button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
          {L.map((d, di) => {
            const dt = new Date(monday); dt.setDate(monday.getDate() + di);
            return (
              <div key={di} className="rounded-xl overflow-hidden ring-1 ring-white/10">
                <div className="text-center py-2 text-white font-semibold text-sm" style={{ background: "#ea580c" }}>{d}<div className="text-[10px] font-normal text-white/80">{pad(dt.getDate())} {ML[dt.getMonth()]}</div></div>
                {["morning", "afternoon"].map((mo, mi) => (
                  <div key={mo} className="p-2 min-h-[90px] border-t border-white/10" data-testid={`cuisine-planning-cell-${di}-${mo}`}>
                    <div className="text-[10px] uppercase font-bold text-orange-300/70">{MO[mi]}</div>
                    {cell(di, mo).map((e) => (
                      <div key={e.id} className="mt-1.5 rounded-lg bg-white/[0.06] ring-1 ring-white/10 p-2 group" data-testid={`cuisine-planning-entry-${e.id}`}>
                        <div className="flex items-start justify-between gap-1">
                          <div className="font-semibold text-xs leading-tight flex items-center gap-1">{!e.can_edit && <Lock className="h-2.5 w-2.5 opacity-50 shrink-0" />}{e.name}</div>
                          {e.can_edit && (
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 shrink-0">
                              <button data-testid={`cuisine-planning-edit-${e.id}`} onClick={() => edit(e)} className="text-sky-400"><Pencil className="h-3 w-3" /></button>
                              <button data-testid={`cuisine-planning-del-${e.id}`} onClick={() => remove(e)} className="text-red-400"><Trash2 className="h-3 w-3" /></button>
                            </div>
                          )}
                        </div>
                        {(e.start || e.place) && <div className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5">{e.start && <><Clock className="h-2.5 w-2.5" />{e.start}{e.end ? `-${e.end}` : ""}</>}{e.place && <><MapPin className="h-2.5 w-2.5 ml-1" />{e.place}</>}</div>}
                        {(e.responsables || e.created_by_name) && <div className="mt-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-medium bg-orange-500/20 text-orange-200"><Users className="h-2.5 w-2.5" />{e.responsables || e.created_by_name}</div>}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Version imprimable (fond blanc) */}
      <div id="cuisine-print" className="hidden" style={{ background: "#fff", color: "#1e293b", padding: 12, fontFamily: "Georgia, serif" }}>
        <h1 style={{ textAlign: "center", fontSize: 22, color: "#c2410c", marginBottom: 10 }}>{t("nav.cuisinePlanning")} — {maison} · {period}</h1>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead><tr>{L.map((d, di) => { const dt = new Date(monday); dt.setDate(monday.getDate() + di); return <th key={di} style={{ border: "1px solid #fdba74", background: "#ffedd5", padding: 6, color: "#9a3412" }}>{d} {pad(dt.getDate())}/{pad(dt.getMonth() + 1)}</th>; })}</tr></thead>
          <tbody>
            {["morning", "afternoon"].map((mo, mi) => (
              <tr key={mo}>
                {L.map((_, di) => (
                  <td key={di} style={{ border: "1px solid #fdba74", padding: 6, verticalAlign: "top", width: `${100 / 7}%` }}>
                    <div style={{ fontWeight: 700, fontSize: 9, textTransform: "uppercase", color: "#ea580c" }}>{MO[mi]}</div>
                    {cell(di, mo).map((e) => (
                      <div key={e.id} style={{ marginTop: 4 }}>
                        <div style={{ fontWeight: 700 }}>{e.name}</div>
                        <div style={{ color: "#64748b", fontSize: 10 }}>{[e.start && `${e.start}${e.end ? `-${e.end}` : ""}`, e.place, e.responsables].filter(Boolean).join(" · ")}</div>
                      </div>
                    ))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("cuisine.copyWeek")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t("cuisine.copyWeekHint", { period })}</p>
          <div><Label>{t("cuisine.copyWeekTo")}</Label>
            <Input type="date" data-testid="cuisine-copy-target" value={copyTarget} onChange={(e) => setCopyTarget(e.target.value)} className="mt-1" /></div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCopyOpen(false)}>{t("planning.cancel")}</Button>
            <Button data-testid="cuisine-copy-confirm" onClick={copyWeek} disabled={!copyTarget}><Copy className="h-4 w-4 mr-1.5" /> {t("cuisine.copyWeek")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
